import { HtmlParser } from '@calendly/html-parser'
import { I18n } from '@calendly/i18n'
import { useDeviceType } from '@calendly/ui/hooks/use-device-type'
import { useLocation } from 'react-router-dom'

import { useGetUserQuery } from 'common'
import { MI_MEETINGS_TOUR, handleCheckStepEnabled } from 'features/meeting_intelligence'
import type { Meeting } from 'features/meetings/types'
import { useScope } from 'hooks/meetings/use_scope'


import { isMultiHost } from './utils'
import { getParticipantText, isExternalCalendarSource } from '../../utils'

const getHostName = (event: Meeting, scopeType: string | null, scopeId: string | null) => {
  const { eventMemberships } = event
  const hasFirstMember = eventMemberships?.length > 0

  if (hasFirstMember && !isMultiHost(event) && (scopeType !== 'user' || scopeId !== 'me')) {
    return eventMemberships[0].name || eventMemberships[0].email
  }
  return null
}

/** True when the list is not "My Calendly" (user + me): teammate user tab, team, org, etc. */
const isDelegatedMeetingsListContext = (scopeType: string | null, scopeId: string | null) => {
  if (scopeType === 'user') {
    return scopeId != null && scopeId !== 'me'
  }
  return scopeType != null
}

/**
 * Derives display data for the `MeetingSummary` component.
 *
 * On teammate / team / org meeting lists, non-host viewers see only the invitee /
 * group attendee line (e.g. `with [name]`): the scope already implies the host, so we
 * omit `hosted by [host]`. My Calendly (user + me) keeps a single `getParticipantText`
 * line. Group list cards pass empty invitees into `getParticipantText` so the subtitle is
 * not first-invitee copy. Also exposes flags for conditional rendering: `cancelled`,
 * `isGroup`, `isPhone`, and `showStepThree` (MI onboarding tour).
 *
 * @param event - The meeting to derive summary data from
 */
export const useMeetingSummaryData = (event: Meeting) => {
  const {
    cancelled,
    eventGuestEmails: emails,
    eventType: { kind } = {},
    source,
    ownerUserUuid,
    invitees,
    host,
    filledCount,
  } = event
  const isGroup = kind === 'group'
  const inviteeName = event?.invitee?.name

  const { data: currentUser } = useGetUserQuery(undefined)
  const { scopeType, scopeId } = useScope()
  const location = useLocation()
  const isPhone = useDeviceType('isPhone')
  const showStepThree = handleCheckStepEnabled(location, MI_MEETINGS_TOUR.STEP_THREE)

  const isExternalMeeting = isExternalCalendarSource(source)
  const isHostMeeting = isExternalMeeting
    ? host?.email === currentUser?.email
    : ownerUserUuid === currentUser?.uuid

  const useHostStyleInviteeSubtitle = !isHostMeeting && !isExternalMeeting && isDelegatedMeetingsListContext(scopeType, scopeId)

  const rawHostName = getHostName(event, scopeType, scopeId)
  const hostName = useHostStyleInviteeSubtitle ? null : rawHostName

  const participantText = useHostStyleInviteeSubtitle
    ? null
    : getParticipantText({
      isHostMeeting,
      source,
      invitees: isGroup ? [] : invitees,
      host,
      ...(!isGroup && { guestEmails: emails }),
    })

  const inviteeText = inviteeName && (
    emails?.length
      ? HtmlParser(I18n.t('publisher.meetings.link.with_and_guests', { name: inviteeName, count: emails.length }))
      : HtmlParser(I18n.t('publisher.meetings.link.with', { name: inviteeName }))
  )

  // List card: with a non-empty `invitees` array (typical for legacy search API) we still use
  // `invitees.length` only. When the array is empty, use `filledCount` (supergraph list often omits
  // invitee rows; legacy FF-off paths with full invitee arrays are unchanged).
  // Do not use `length ?? filledCount` — `0` is not nullish, so a valid `filledCount` was skipped.
  // Coerce to number: i18n-js pluralization uses `switch (count)` (strict), so string "1"
  // would wrongly pick `other` → "with 1 attendees" instead of "with 1 attendee".
  const listInviteeLen = invitees?.length ?? 0
  const rawGroupInviteeCount = listInviteeLen > 0
    ? listInviteeLen
    : (Number(filledCount) || 0)
  const groupInviteeCount = Number(rawGroupInviteeCount) || 0
  const groupText = isGroup && I18n.t('publisher.meetings.link.group.unlimited', {
    count: groupInviteeCount,
  })

  return {
    cancelled,
    isGroup,
    hostName,
    participantText,
    inviteeText,
    groupText,
    showStepThree,
    isPhone,
  }
}
